import { useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';

import { ModuleBase } from '@blocks';
import { Segmentation } from '@core';
import { ActivitySwing, GraphsNode } from '@data';
import { useGraphData } from '@hooks';
import { useTranslate } from '@i18n';
import { useSwingAnalysis } from '@queries';
import { activeUserSettingsAtom, activityUnitAtom } from '@store';
import { Graph, Spinner, ToggleSelectButton } from '@ui';

type ActivityReportGraphsProps = {
    graphNodes?: GraphsNode[];
    swings?: ActivitySwing[];
};

export const ActivityReportGraphs = ({ graphNodes, swings }: ActivityReportGraphsProps) => {
    return graphNodes?.map((graphNode) => (
        <ActivityReportGraphInner key={graphNode.id} graphNode={graphNode} swings={swings} />
    ));
};

type ActivityReportGraphInnerProps = {
    swings?: ActivitySwing[];
    graphNode?: GraphsNode;
};

const ActivityReportGraphInner = ({ swings, graphNode }: ActivityReportGraphInnerProps) => {
    const [swing, setSwing] = useState(swings?.[0]);
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const { data: selectedAnalysis, isLoading } = useSwingAnalysis(swing?.uuid);

    const options = useMemo(
        () => (
            <ToggleSelectButton
                prefix="Swings"
                options={swings?.sort((a, b) => a.number - b.number)?.map((d) => `${d.number}`) ?? []}
                selectedOption={`1`}
                onChange={(value) => setSwing(swings?.find((d) => d.number === +(value ? value : 0)))}
                hasNone={false}
            />
        ),
        [swings],
    );

    const { graphData, segmentation, graphLines } = useGraphData({
        nodeId: graphNode?.id,
        userPreferredUnit: activityUnit,
        selectedAnalysis: selectedAnalysis?.fullAnalysis,
        comparisonAnalysis: null,
    });

    const t = useTranslate();

    return (
        <ModuleBase title={t(graphNode?.i18n ?? '-')} options={options}>
            {!isLoading && graphData ? (
                <Graph
                    data={graphData}
                    lines={graphLines}
                    segmentation={segmentation as { current: Segmentation; comparison: Segmentation | null }}
                    showComparison={userSettings.showComparison}
                />
            ) : (
                <Spinner size="l" />
            )}
        </ModuleBase>
    );
};
